import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Entity model with allowed actions.
 */
export interface EntityAction<TEntity> {
  /**
   * The entity (payload).
   */
  value: TEntity;
}


/**
 * Data upload.
 */
export interface Upload {
  // common props
  /**
   * Data upload ID.
   */
  id: string;

  /**
   * User who created upload.
   */
  creator: User;

  /**
   * Data Upload Info section.
   */
  uploadInfo: UploadInfo;
  /**
   * File Preparation section.
   */
  filePreparation: UploadFilePrep;
  /**
   * Create Tags section.
   */
  tags: UploadTags;
}

/*
 * Data Upload Info section.
 */
export interface UploadInfo {
  /*
   * Data upload display name.
   */
  name: string;
  /*
   * Data upload description.
   */
  comment: string;

  /**
   * Related data collection information.
   */
  collection: CollectionInfo;

  /**
   * Data upload name length constraint.
   */
  nameMaxLength: number;
  /**
   * Name field validation pattern.
   */
  namePattern: string;

  /**
   * Commend field length constraint.
   */
  commentMaxLength: number;
}


/**
 * File Preparation section.
 */
export interface UploadFilePrep {
  /**
   * Uploaded file(s) preparation question.
   */
  dataPreparation: YesNoQuestionBase;
}


/**
 * Create Tags section.
 */
export interface UploadTags {
  /**
   * Tags associated with the upload.
   */
  tags: Tag[];

  /**
   * Name field length constraint.
   */
  nameMaxLength: number;
  /**
   * Name field validation pattern.
   */
  namePattern: string;

  /**
   * Value field length constraint.
   */
  valueMaxLength: number;
  /**
   * Value field validation pattern.
   */
  valuePattern: string;
}

/**
 * Data upload tag.
 */
export interface Tag {
  /**
   * Tag name.
   */
  name: string;
  /**
   * Tag value.
   */
  value: string;
}

/**
 * System user.
 */
export interface User {
  /**
   * User ID.
   */
  id: string;
}

/**
 * Represents question with yes/no answers.
 */
export interface YesNoQuestionBase {
  /**
   * Question answer:
   * true - Yes
   * false - no
   * null - no answer (initial state)
   */
  answer?: boolean;
}


/**
 * Data collection information related to data upload.
 */
export interface CollectionInfo {
  /**
   * Collection ID.
   */
  id: string;
  /**
   * Collection name.
   */
  name: string;

  /**
   * Data originator.
   */
  dataProvider: DataProvider;
}


/**
 * Data provider common properties.
 */
export interface DataProvider {
  /**
   * Data provider ID.
   */
  id: string;

  /**
   * Source country.
   */
  country: Country;
}

/**
 * Country data.
 */
export interface Country {
  /**
   * ISO two-letters code.
   */
  code: string;
}



/**
 * Data upload file information.
 */
export interface UploadFileInfo {
  /**
   * File ID. GUID.
   * Issued by SHC File Upload Service.
   */
  id: string;
  /**
   * Original file name.
   */
  name: string;
  /**
   * File size in bytes.
   */
  size: number;

  /**
   * Specifies if metadata file should be ingested.
   */
  metadata: boolean;
  /**
   * Specifies if file can be removed.
   */
  canDelete: boolean;
  /**
   * UTC date time of upload URL expiration.
   */
  expiresOn: Date;
}


/**
 * Service that encapsulates data upload related operations.
 */
@Injectable({ providedIn: 'root' })
export class UploadService {
  private readonly http = inject(HttpClient);

  /**
   * API base URL for data upload related HTTP requests.
   */
  private readonly baseUrl = `${environment.dl.url}ui/uploads`;

  /**
   * Retrieves blank data upload with allowed actions list.
   *
   * @returns Blank data upload.
   */
  public blank(): Observable<Upload> {
    return this.http.get<EntityAction<Upload>>(`${this.baseUrl}/blank`).pipe(
      map(response => response.value)
    );
  }

  /**
   * Adds new data upload.
   *
   * @param upload Data upload to add.
   * @returns Actual data upload
   */
  public create(upload: Upload): Observable<Upload> {
    return this.http.post<EntityAction<Upload>>(this.baseUrl, upload).pipe(
      map(response => response.value)
    );
  }

  /**
   * Add data upload file.
   *
   * @param upload Data upload.
   * @param file Data upload file.
   * @returns Actual data upload file.
   */
  public addFile(upload: Upload, file: UploadFileInfo): Observable<File> {
    return this.http.post<File>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files`, file);
  }

  /**
   * Remove data upload file.
   *
   * @param upload Data upload.
   * @param fileID Data upload file ID.
   */
  public deleteFile(upload: Upload, fileID: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files/${fileID}`);
  }

  /**
   * Sets data upload to auto submit.
   *
   * @param upload Data upload.
   * @returns Actual data upload.
   */
  public autoSubmit(upload: Upload): Observable<Upload> {
    // tslint:disable-next-line:max-line-length
    return this.http.put<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/auto-submit`, null).pipe(
      map(response => response.value)
    );
  }
}
